import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import {
  Button,
  IconButton,
  Popover,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import React, { useCallback, useState } from 'react'

import useAccessTokenAndShopId from '../../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import {
  useDeleteAdditionalPhoneMutation,
  useUpdateAdditionalPhoneMutation
} from '../../../redux/api'
import { CustomerAdditionalPhoneEntry } from '../../../sdk'
import { loggingAssert } from '../../../utils/handleError/loggingAssert'

interface Props {
  phone: CustomerAdditionalPhoneEntry
  markAsPrimary: (phone: string) => void
  isPrimary: boolean
}

const POPOVER_TIMEOUT = 500

export const AdditionalPhonesTableEntry: React.FC<Props> = ({
  phone,
  markAsPrimary,
  isPrimary
}) => {
  const { accessToken } = useAccessTokenAndShopId()

  const [deleteAdditionalPhone] = useDeleteAdditionalPhoneMutation()

  const [editMode, setEditMode] = useState(false)

  const [updatePhoneEntry, setUpdatePhoneEntry] =
    useState<CustomerAdditionalPhoneEntry>({
      id: phone.id,
      info: {
        customer: phone.info!.customer,
        phone: phone.info!.phone,
        description: phone.info!.description
      }
    })

  const [updateAdditionalPhone] = useUpdateAdditionalPhoneMutation()

  const handleSubmit = useCallback(
    async (isPrimary: boolean) => {
      loggingAssert('Phone id is undefined', updatePhoneEntry.id)
      loggingAssert('customer is undefined', updatePhoneEntry.info!.customer)
      loggingAssert('Phone number is undefined', updatePhoneEntry.info!.phone)
      if (!updatePhoneEntry.info!.description) {
        await updateAdditionalPhone({
          authorization: accessToken,
          customerAdditionalPhoneEntry: {
            id: phone.id,
            info: {
              customer: phone.info!.customer,
              phone: updatePhoneEntry.info!.phone,
              description: 'Additional Phone'
            }
          }
        })
      } else {
        const response = await updateAdditionalPhone({
          authorization: accessToken,
          customerAdditionalPhoneEntry: updatePhoneEntry
        })
        if (isPrimary && 'data' in response) {
          markAsPrimary(updatePhoneEntry.info!.phone!)
        }
      }
      setEditMode(false)
    },
    [
      accessToken,
      phone.id,
      phone.info,
      updateAdditionalPhone,
      updatePhoneEntry,
      markAsPrimary
    ]
  )

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const openPopover = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      navigator.clipboard.writeText(updatePhoneEntry.info!.phone || '')
      setAnchorEl(event.currentTarget)
      setTimeout(() => {
        setAnchorEl(null)
      }, POPOVER_TIMEOUT)
    },
    [updatePhoneEntry.info]
  )

  return (
    <>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}>
        <Typography sx={{ p: 2 }}>Copied to clipboard</Typography>
      </Popover>
      {editMode ? (
        <TableRow>
          <TableCell>
            <TextField
              label="Phone Description"
              variant="standard"
              value={updatePhoneEntry.info!.description}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setUpdatePhoneEntry({
                  id: phone.id,
                  info: {
                    customer: phone.info!.customer,
                    phone: updatePhoneEntry.info!.phone,
                    description: event.target.value
                  }
                })
              }
            />
          </TableCell>
          <TableCell align="right">
            <TextField
              label="Phone Number"
              variant="standard"
              value={updatePhoneEntry.info!.phone}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setUpdatePhoneEntry({
                  id: phone.id,
                  info: {
                    customer: phone.info!.customer,
                    phone: event.target.value,
                    description: updatePhoneEntry.info!.description
                  }
                })
              }
            />
          </TableCell>
          <TableCell align="right">
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="flex-end">
              <Button
                onClick={() => handleSubmit(isPrimary)}
                disabled={
                  !updatePhoneEntry.id ||
                  !updatePhoneEntry.info!.customer ||
                  !updatePhoneEntry.info!.phone
                }>
                Save
              </Button>
              <Button onClick={() => setEditMode(false)}>Cancel</Button>
            </Stack>
          </TableCell>
        </TableRow>
      ) : (
        <TableRow>
          <TableCell>{phone.info?.description}</TableCell>
          <TableCell align="right">{phone.info?.phone}</TableCell>
          <TableCell align="right">
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="flex-end">
              <Tooltip title="Clipboard">
                <IconButton onClick={openPopover}>
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton onClick={() => setEditMode(true)}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  onClick={() =>
                    deleteAdditionalPhone({
                      authorization: accessToken,
                      customer: phone.info!.customer!,
                      id: phone.id!
                    })
                  }>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
              {isPrimary ? (
                <Tooltip title="Mark as primary">
                  <IconButton onClick={() => markAsPrimary(phone.info!.phone!)}>
                    <StarIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Mark as primary">
                  <IconButton onClick={() => markAsPrimary(phone.info!.phone!)}>
                    <StarBorderIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}
